import React from "react"
import { DragDropProvider } from "@dnd-kit/react"
import { move } from "@dnd-kit/helpers"
import type { Step, StepConfig, SelectOption, VariableGroups } from "./types"
import { StepCard } from "./StepCard"

interface SortableStepListProps {
  steps: Step[]
  onReorder: (newSteps: Step[]) => void
  onUpdate: (index: number, updates: Partial<Step>) => void
  onUpdateConfig: (index: number, updates: Partial<StepConfig>) => void
  onChangeType: (index: number, newType: string) => void
  onRemove: (index: number) => void
  channels: SelectOption[]
  tags: SelectOption[]
  ignoreReasons: SelectOption[]
  canForward: boolean
  canComposeActions: boolean
  canExecuteHttpRequest: boolean
  billingUrl: string
  variableHints?: VariableGroups
}

export function SortableStepList({
  steps,
  onReorder,
  onUpdate,
  onUpdateConfig,
  onChangeType,
  onRemove,
  channels,
  tags,
  ignoreReasons,
  canForward,
  canComposeActions,
  canExecuteHttpRequest,
  billingUrl,
  variableHints,
}: SortableStepListProps) {
  const visibleSteps = steps.filter((s) => !s._destroy)

  function handleDragEnd(event: Parameters<NonNullable<React.ComponentProps<typeof DragDropProvider>["onDragEnd"]>>[0]) {
    if (event.canceled) return

    const reordered = move(visibleSteps, event)
    const destroyed = steps.filter((s) => s._destroy)
    onReorder([
      ...reordered.map((s: Step, i: number) => ({ ...s, position: i + 1 })),
      ...destroyed,
    ])
  }

  return (
    <DragDropProvider onDragEnd={handleDragEnd}>
      {visibleSteps.map((step, visIdx) => {
        const realIdx = steps.indexOf(step)
        const sortableId = step.id || `new-${realIdx}`
        const priorKeys = new Set(visibleSteps.slice(0, visIdx).map((priorStep) => priorStep.reference_key).filter(Boolean))
        const stepVariableHints = variableHints
          ? {
              ...variableHints,
              steps: variableHints.steps?.filter((hint) => priorKeys.has(hint.path.split("/")[2])),
            }
          : undefined
        return (
          <StepCard
            key={sortableId}
            step={step}
            index={realIdx}
            displayIndex={visIdx}
            sortableId={sortableId}
            onUpdate={onUpdate}
            onUpdateConfig={onUpdateConfig}
            onChangeType={onChangeType}
            onRemove={onRemove}
            channels={channels}
            tags={tags}
            ignoreReasons={ignoreReasons}
            canForward={canForward}
            canComposeActions={canComposeActions}
            canExecuteHttpRequest={canExecuteHttpRequest}
            billingUrl={billingUrl}
            variableHints={stepVariableHints}
          />
        )
      })}
    </DragDropProvider>
  )
}
